// What the first load shows when the server gives no usable answer and this device keeps no session: HolyDeck is
// most likely being updated. The page asks again by itself every few seconds, for a few minutes, then offers
// Try again. A signed-in page never shows this; its top bar pill names the same outage.

import { useEffect, useRef, useState } from 'preact/hooks';

import { serverUpdating } from '../app-state.js';
import { t } from '../i18n.js';
import { retryBoot, SERVER_RETRY_GIVE_UP_MS, SERVER_RETRY_MS } from '../request.js';

import type { JSX } from 'preact';

export function ServerUpdating(): JSX.Element {
  const since = serverUpdating.value;
  const [now, setNow] = useState(() => Date.now());
  const [nextAt, setNextAt] = useState(() => Date.now() + SERVER_RETRY_MS);
  const [asking, setAsking] = useState(false);
  const [pressed, setPressed] = useState(false);
  const asked = useRef(false);
  const primary = useRef<HTMLButtonElement>(null);
  const wasGaveUp = useRef(false);
  const mounted = useRef(true);
  const gaveUpBy = (moment: number): boolean => since !== undefined && moment - since >= SERVER_RETRY_GIVE_UP_MS;
  const gaveUp = gaveUpBy(now);

  const ask = (restart: boolean, byPress = false): void => {
    if (asked.current) {
      return;
    }

    asked.current = true;
    setAsking(true);
    setPressed(byPress);
    void retryBoot(restart).catch(() => undefined).finally(() => {
      asked.current = false;
      if (mounted.current) {
        setAsking(false);
        setPressed(false);
        setNow(Date.now());
        setNextAt(Date.now() + SERVER_RETRY_MS);
      }
    });
  };

  useEffect(() => {
    const timer = setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= nextAt && !gaveUpBy(current)) {
        ask(false);
      }
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  });

  useEffect(() => (): void => {
    mounted.current = false;
  }, []);

  useEffect(() => {
    if (wasGaveUp.current !== gaveUp) {
      primary.current?.focus();
    }

    wasGaveUp.current = gaveUp;
  }, [gaveUp]);

  if (gaveUp) {
    return (
      <div class="card sign-in-card access-small server-updating">
        <h1>{t('serverUpdating.title')}</h1>
        <p role="alert">{t('loadFailure.unreachable')}</p>
        <div class="form-actions">
          <button ref={primary} type="button" class="button-primary" aria-disabled={asking} onClick={() => ask(true, true)}>{t('loadFailure.retry')}</button>
        </div>
        <p class="server-updating-note">{t('serverUpdating.note')}</p>
      </div>
    );
  }

  const seconds = Math.max(0, Math.ceil((nextAt - now) / 1000));
  return (
    <div class="card sign-in-card access-small server-updating">
      <h1>{t('serverUpdating.title')}</h1>
      <p>{t('serverUpdating.body')}</p>
      <p class="server-updating-note">{asking ? t('loadFailure.retrying') : t('serverUpdating.countdown', { seconds: String(seconds) })}</p>
      <p role="status" class="visually-hidden">{pressed ? t('loadFailure.retrying') : ''}</p>
      <div class="form-actions">
        <button ref={primary} type="button" class="button-primary" aria-disabled={asking} onClick={() => ask(false, true)}>{t('serverUpdating.tryNow')}</button>
      </div>
      <p class="server-updating-note">{t('serverUpdating.note')}</p>
    </div>
  );
}
